'use client'

import type { Route } from 'next'
import { useRouter } from 'next/navigation'
import { FileUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import type { FlowType } from '@/features/cases'
import { SERVICE_META } from '@/features/investor/components/home/service-meta'

/**
 * Offers the services an investor doesn't yet own. Ported from
 * `nivex-design/apps/nivex-web-v3/src/components/ServiceUpsell.tsx`, minus its
 * advisor-recommendation variant (the `ServiceRecommendation` domain — "advisor recommends this
 * service" copy + accept/dismiss actions — doesn't exist anywhere in this app, frontend or
 * backend; see PROGRESS.md). Every offered row here uses the plain "upload your document" prompt.
 */
export function ServiceUpsell({ ownedFlows }: { ownedFlows: Set<FlowType> }) {
  const router = useRouter()
  const offered = (Object.keys(SERVICE_META) as FlowType[]).filter((flow) => !ownedFlows.has(flow))
  if (offered.length === 0) return null

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-medium">More ways Nivex can help</p>
      <div className="flex flex-col gap-2">
        {offered.map((flow) => {
          const service = SERVICE_META[flow]
          return (
            <Card
              key={flow}
              size="sm"
              className="flex-col items-stretch gap-3 sm:flex-row sm:items-center"
            >
              <CardContent className="flex flex-1 items-center gap-3 py-0">
                <span className="bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-lg">
                  <FileUp className="size-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{service.title}</p>
                  <p className="text-muted-foreground truncate text-xs">{service.promptCopy}</p>
                </div>
              </CardContent>
              <div className="px-(--card-spacing) sm:pr-(--card-spacing) flex items-center gap-1.5 sm:shrink-0 sm:px-0">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full sm:w-auto"
                  onClick={() => router.push(service.startRoute as Route)}
                >
                  {flow === 'build' ? 'Get started' : 'Upload'}
                </Button>
              </div>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
